/**
 * G17 issue tracker and release integrations (V38). Provider-agnostic contract for GitHub Issues/
 * Releases now and JIRA, Linear, GitLab, Azure DevOps later (docs/ux/issue-tracker-integrations.md).
 *
 * Every normalized record below is tracker CONTENT: it lives only on the trusted daemon and inside
 * encrypted entities; the hub never sees it. Titles, bodies, comments, labels and release notes are
 * attacker-controllable (anyone who can open an issue writes them) and are quarantined data, never
 * instructions — agent-bound text goes through the G9 brief renderer.
 *
 * Shape rules: fields every provider can fill are normalized; anything else goes in `ext`
 * (bounded, provider-namespaced, never interpreted by generic code). Additive-only.
 */
import { z } from 'zod';
import { FeedbackTier, OpRef, SourceUrl } from './feedback.js';
const Ts = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const Text = (max) => z.string().max(max);
export const TRACKER_PROVIDERS = ['github', 'jira', 'linear', 'gitlab', 'azure-devops'];
export const TrackerProviderKind = z.enum(TRACKER_PROVIDERS);
/** Max normalized body/comment length; longer text is cut and `truncated` set. */
export const TRACKER_MAX_BODY = 65_536;
/**
 * What a provider (as connected, with the granted scopes) can do. Generic code branches on these,
 * never on `provider`. `'none'` < `'read'` < `'write'`.
 */
export const TrackerAccess = z.enum(['none', 'read', 'write']);
export const TrackerCapabilities = z.strictObject({
    issues: TrackerAccess,
    comments: TrackerAccess,
    labels: TrackerAccess,
    /** Planning containers the provider has, most specific first (GitHub: milestone; JIRA: sprint, epic, fixVersion). */
    planning: z.array(z.enum(['milestone', 'sprint', 'epic', 'cycle', 'project', 'version'])).max(6),
    planningAccess: TrackerAccess,
    releases: TrackerAccess,
    /** Custom workflow states beyond open/closed (JIRA, Linear, Azure DevOps). */
    customWorkflow: z.boolean(),
    /** Linked PR/MR discovery for an issue. */
    prLinks: TrackerAccess,
    /** Parent/child issues (GitHub sub-issues, JIRA epics/subtasks, Linear parent). */
    hierarchy: z.boolean(),
    /** Server-side `updated since` filtering, so polling is incremental. */
    incrementalSince: z.boolean(),
    /** Conditional requests (ETag/If-None-Match) that do not consume rate limit when unchanged. */
    conditionalGet: z.boolean(),
    /** The provider can push signed webhooks (delivery is still daemon-side; see design doc §1.6). */
    webhooks: z.boolean(),
});
/** A stable pointer to one tracker object. `key` is what humans type ("#42", "PROJ-12", "ENG-7"). */
export const TrackerRef = z.strictObject({
    provider: TrackerProviderKind,
    /** API host ("api.github.com", "acme.atlassian.net"). */
    instance: Text(253),
    /** Repo ("owner/name"), JIRA project key, Linear team key. */
    container: Text(200),
    key: Text(64),
    /** Provider-stable id (GitHub node/number, JIRA id, Linear uuid). */
    id: Text(128),
    url: Text(2000),
});
/**
 * How the provider says this person relates to the container. Providers authenticate their users,
 * so `association` is identity-verified; the trust TIER it maps to is still capped by the
 * connection's `maxTier` and the project's G9 trust lists.
 */
export const TrackerAssociation = z.enum([
    'owner',
    'member',
    'collaborator',
    'contributor',
    'none',
    'unknown',
]);
export const TrackerUser = z.strictObject({
    id: Text(128),
    login: Text(200).optional(),
    display: Text(200).optional(),
    association: TrackerAssociation,
    bot: z.boolean(),
});
/**
 * Normalized workflow position. `category` is what Viko reasons about; `raw` is the provider's own
 * state name (JIRA "In Review", Linear "Triage") shown to the user and written back verbatim.
 */
export const TrackerStatusCategory = z.enum(['open', 'in_progress', 'done', 'cancelled']);
export const TrackerStatus = z.strictObject({
    category: TrackerStatusCategory,
    raw: Text(100),
});
export const TrackerLabel = z.strictObject({
    name: Text(200),
    color: z
        .string()
        .regex(/^[0-9a-f]{6}$/i)
        .optional(),
    description: Text(1000).optional(),
});
/** Milestone / sprint / epic / cycle / fixVersion: whatever the provider plans work with. */
export const TrackerPlanning = z.strictObject({
    ref: TrackerRef,
    kind: z.enum(['milestone', 'sprint', 'epic', 'cycle', 'project', 'version']),
    title: Text(500),
    description: Text(TRACKER_MAX_BODY).optional(),
    state: z.enum(['open', 'closed']),
    dueAt: Ts.optional(),
    openIssues: z.number().int().nonnegative().optional(),
    closedIssues: z.number().int().nonnegative().optional(),
});
export const TrackerPrLink = z.strictObject({
    url: Text(2000),
    key: Text(64).optional(),
    state: z.enum(['open', 'merged', 'closed', 'unknown']),
    /** `closes` when the PR/commit uses a closing keyword ("Fixes #42"). */
    relation: z.enum(['closes', 'mentions']),
});
/** Provider-specific extras (JIRA custom fields, GitHub reactions, Linear estimate). Bounded. */
export const TrackerExt = z.record(z.string().max(100), z.unknown());
export const TrackerIssue = z.strictObject({
    ref: TrackerRef,
    title: Text(1000),
    body: Text(TRACKER_MAX_BODY),
    truncated: z.boolean(),
    status: TrackerStatus,
    labels: z.array(Text(200)).max(100),
    author: TrackerUser,
    assignees: z.array(TrackerUser).max(50),
    planning: z.array(TrackerRef).max(10),
    parent: TrackerRef.optional(),
    prLinks: z.array(TrackerPrLink).max(50),
    commentCount: z.number().int().nonnegative(),
    /** The provider locked the conversation (e.g. heated/spam). */
    locked: z.boolean(),
    createdAt: Ts,
    updatedAt: Ts,
    closedAt: Ts.optional(),
    ext: TrackerExt,
});
export const TrackerComment = z.strictObject({
    id: Text(128),
    issue: TrackerRef,
    author: TrackerUser,
    body: Text(TRACKER_MAX_BODY),
    truncated: z.boolean(),
    url: Text(2000),
    createdAt: Ts,
    updatedAt: Ts,
});
export const TrackerReleaseAsset = z.strictObject({
    name: Text(500),
    size: z.number().int().nonnegative(),
    contentType: Text(200),
    url: Text(2000),
});
export const TrackerRelease = z.strictObject({
    ref: TrackerRef,
    tag: Text(250),
    name: Text(500),
    notes: Text(TRACKER_MAX_BODY),
    truncated: z.boolean(),
    draft: z.boolean(),
    prerelease: z.boolean(),
    /** Commit-ish the tag points (or will point) at. */
    target: Text(250).optional(),
    createdAt: Ts,
    publishedAt: Ts.optional(),
    assets: z.array(TrackerReleaseAsset).max(100),
    ext: TrackerExt,
});
/** Provider-reported rate limit after the last call (daemon-side scheduling only). */
export const TrackerRateLimit = z.strictObject({
    remaining: z.number().int().nonnegative(),
    limit: z.number().int().nonnegative(),
    resetAt: Ts,
});
export const TrackerIssueDraft = z.strictObject({
    title: Text(1000).min(1),
    body: Text(TRACKER_MAX_BODY),
    labels: z.array(Text(200)).max(100).default([]),
    planning: z.array(TrackerRef).max(10).default([]),
});
export const TrackerIssuePatch = z.strictObject({
    title: Text(1000).min(1).optional(),
    body: Text(TRACKER_MAX_BODY).optional(),
    status: TrackerStatus.optional(),
    labels: z.array(Text(200)).max(100).optional(),
    planning: z.array(TrackerRef).max(10).optional(),
});
export const TrackerReleaseDraft = z.strictObject({
    tag: Text(250).min(1),
    name: Text(500),
    notes: Text(TRACKER_MAX_BODY),
    target: Text(250).optional(),
    prerelease: z.boolean().default(false),
});
// ---- Connection config (lives in an encrypted per-project entity; values are op:// refs) ----
/**
 * Credentials. Secrets are always 1Password references resolved only on the daemon through the
 * secrets broker. `gh-cli` borrows the machine's existing `gh auth` session (read prototypes and
 * single-user setups); the recommended long-term option is a fine-grained PAT or a GitHub App.
 */
export const TrackerAuth = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('none') }),
    z.strictObject({ kind: z.literal('gh-cli') }),
    z.strictObject({ kind: z.literal('token'), token: OpRef }),
    z.strictObject({
        kind: z.literal('github-app'),
        appId: z.string().regex(/^\d{1,20}$/),
        installationId: z.string().regex(/^\d{1,20}$/),
        privateKey: OpRef,
    }),
    z.strictObject({
        kind: z.literal('basic-token'),
        /** JIRA Cloud: account email + API token. */
        username: z.string().min(1).max(320),
        token: OpRef,
    }),
    z.strictObject({
        kind: z.literal('oauth'),
        clientId: z.string().min(1).max(200),
        refreshToken: OpRef,
    }),
]);
const Slug = z.string().regex(/^[A-Za-z0-9_.-]{1,100}$/);
export const TrackerTarget = z.discriminatedUnion('provider', [
    z.strictObject({
        provider: z.literal('github'),
        /** Only github.com (no GitHub Enterprise). */
        owner: Slug,
        repo: Slug,
    }),
    z.strictObject({
        provider: z.literal('jira'),
        baseUrl: SourceUrl,
        projectKey: z.string().regex(/^[A-Z][A-Z0-9_]{0,19}$/),
    }),
    z.strictObject({ provider: z.literal('linear'), teamKey: z.string().regex(/^[A-Z0-9]{1,10}$/) }),
]);
/** Per-field sync direction (design doc §2.3). `import` = tracker → Viko, `export` = Viko → tracker. */
export const TrackerSyncDirection = z.enum(['off', 'import', 'export', 'both']);
export const TrackerConnectionId = z.string().regex(/^[a-z0-9-]{1,40}$/);
export const TrackerConnection = z.strictObject({
    id: TrackerConnectionId,
    name: z.string().trim().min(1).max(80),
    enabled: z.boolean(),
    /** The one machine that syncs this connection (single writer; avoids duplicate polls). */
    machineId: z.string().regex(/^[A-Za-z0-9:_-]{1,128}$/),
    target: TrackerTarget,
    auth: TrackerAuth,
    /** Highest G9 trust tier any tracker author can reach. */
    maxTier: FeedbackTier,
    pollMinutes: z
        .number()
        .int()
        .min(2)
        .max(24 * 60)
        .default(10),
    sync: z
        .strictObject({
        issues: TrackerSyncDirection.default('import'),
        status: TrackerSyncDirection.default('import'),
        comments: TrackerSyncDirection.default('off'),
        labels: TrackerSyncDirection.default('import'),
        planning: TrackerSyncDirection.default('import'),
        closeOnLand: z.boolean().default(false),
        releaseDrafts: z.boolean().default(false),
    })
        .default({
        issues: 'import',
        status: 'import',
        comments: 'off',
        labels: 'import',
        planning: 'import',
        closeOnLand: false,
        releaseDrafts: false,
    }),
});
/** Normalizes body text: strips NUL/C0 controls (except tab/newline), caps length. */
export function trackerText(v, max = TRACKER_MAX_BODY) {
    const s = typeof v === 'string'
        ? // biome-ignore lint/suspicious/noControlCharactersInRegex: stripping control characters is the point
            v.replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, '')
        : '';
    return s.length > max ? { text: s.slice(0, max), truncated: true } : { text: s, truncated: false };
}
/**
 * Default association → trust tier, capped by the connection's `maxTier`. Explicit G9 trust-list
 * matches (`github:<login>`) may raise a user up to the cap; bots and unknown are always public.
 */
export function trackerTier(user, maxTier) {
    const base = user.bot
        ? 'public'
        : user.association === 'owner' || user.association === 'member'
            ? 'dev'
            : user.association === 'collaborator'
                ? 'tester'
                : 'public';
    const order = ['dev', 'tester', 'public'];
    return order.indexOf(base) >= order.indexOf(maxTier) ? base : maxTier;
}
//# sourceMappingURL=issue-trackers.js.map